import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Col,
    Collapse,
    Divider,
    Form,
    Grid,
    Input,
    InputNumber,
    Modal,
    Pagination,
    Row,
    Skeleton,
    Slider,
    Space,
    theme,
    message,
} from 'antd';
import moment from 'moment';
import { DownloadOutlined } from '@ant-design/icons';
import handleDownloadFile from '@/utils/downloadFile';
import dayjs from 'dayjs';
import { Panel } from '@/components/Layout';
import { useStoreManagementSummary } from '@/store/Store/useStoreManagementSummary';
import { InventoryType, ParamsStoreManagement } from '@/store/Store/types';
import { Store } from '@inhouse-market/sdk';
import { isRecaptchaNotResolved } from '@/contexts/Recaptcha';
import { topTitleContext } from '../../contexts/TopTitle';
import * as S from './style';
import TableStoreMAnagement from './columns';
import { menuContext } from '../../contexts/Menu';
import { useStoreManagement } from '../../store/Store/useStoreManagement';
import { currencyFormatter } from '../../helpers/currencyParser';
import { useStoreEdit } from '../../store/Store/useStoreEdit';
import { useAuth } from '../../contexts/Auth';
import { useReport } from '../../store/Store/CreateExcel/useReportStore';
import { useExcelExport } from '../../store/salles/GetExcel/useExcelExport';
import { ResponsiveText } from './components/ResponsiveText';
import { FilterStoreManagement } from './components/Filters';

interface FileGenerated {
    status: 'completed' | 'processing' | 'pending' | 'error';
}

export const getStatusText = (status: FileGenerated['status']): string => {
    switch (status) {
        case 'completed':
            return '(Pronto para baixar)';
        case 'processing':
        case 'pending':
            return '(Baixando - Aguarde)';
        case 'error':
            return '(Erro - Tentar novamente)';
        default:
            return '';
    }
};

const StoreManagementPage = () => {
    /* context */
    const { titleValue } = useContext(topTitleContext);
    const menu = useContext(menuContext);

    const { data: dataAuth } = useAuth();
    const [fontSizeDynamic, setFontSizeDynamic] = useState(0);

    const breakpoints = Grid.useBreakpoint();
    const { token } = theme.useToken();
    const [form] = Form.useForm();

    const { mutateAsync, isLoading: isLoadingEditStore } = useStoreEdit();

    const { mutateAsync: useReportStoreMutation, isLoading: isLoadingReport } =
        useReport();

    const { data: exportedExcel } = useExcelExport({
        userId: dataAuth.user?.id,
        type: 'stores',
    });

    const [openMpdal, setOpenModal] = useState(false);
    const [storesSelected, setStoresSelected] = useState([]);
    const [shopManagersEdit, setShopManagersEdit] = useState({
        shopManagerBlocked: false,
        active: false,
        opened: false,
    });
    const [statusStores, setStatusStores] = useState({
        isBlocked: 50,
        opened: 50,
        active: 50,
    });
    const [storesChecked, setStoresChecked] = useState<
        Array<Store.StoreManagement>
    >([]);
    const [meta, setMeta] = useState({
        page: 1,
        last_page: 1,
        total: 0,
        perPage: 10,
    });

    const width = useMemo(() => {
        return menu.isCollapsed
            ? window.innerWidth - 249 - 82
            : window.innerWidth - 79 - 82;
    }, [menu.isCollapsed]);

    const [filters, setFilters] = useState<ParamsStoreManagement>({
        name: '',
        shopmanagerEmail: '',
        totemEnum: undefined,
        city: undefined,
        state: undefined,
        open: true,
        active: true,
        firstSaleDate: undefined,
        saleInLastSevenDays: undefined,
        inventory: ['store-physical', 'null'],
        orderBy: undefined,
        orderDir: undefined,
    });
    const [initialValues, setInitialValues] = useState<{
        open: boolean | undefined;
        active: boolean | undefined;
        inventory: InventoryType[] | undefined;
        saleInLastSevenDays: boolean | string;
        billingType: null | string;
        orderBy: undefined;
        orderDir: undefined;
    }>({
        open: true,
        active: true,
        inventory: ['store-physical', 'null'],
        saleInLastSevenDays: '',
        billingType: null,
        orderBy: undefined,
        orderDir: undefined,
    });

    const { data, isLoading } = useStoreManagement({
        ...filters,
        ...meta,
    });

    const { data: dataSummary, isLoading: isLoadingSummary } =
        useStoreManagementSummary(filters);

    const setCurrentPage = (value: number, pzSize: number) => {
        setMeta((prev) => {
            return { ...prev, page: value, perPage: pzSize };
        });
        setFilters({
            ...filters,
            firstSaleDate: filters?.firstSaleDate?.replace('/', '-'),
        });
    };

    const handleEditStores = async () => {
        try {
            await mutateAsync({
                id: 1,
                storeIds: storesSelected,
                active:
                    statusStores.active === 50 ? null : shopManagersEdit.active,
                isBlocked:
                    statusStores.isBlocked === 50
                        ? null
                        : shopManagersEdit.shopManagerBlocked,
                open:
                    statusStores.opened === 50 ? null : shopManagersEdit.opened,
            });
            setOpenModal(false);
            setShopManagersEdit({
                active: false,
                opened: false,
                shopManagerBlocked: false,
            });
            setStoresChecked([]);
            setStoresSelected([]);
            message.success('Dados atualizado com sucesso!');
        } catch (err) {
            message.error(
                'Aconteceu algum problema. Tente novamente mais tarde',
            );
        }
    };

    const onSelectChange = (newSelectedRowKeys: any) => {
        setStoresSelected(newSelectedRowKeys);

        let storesCheckedArray = [...storesChecked];

        for (let i = 0; i < newSelectedRowKeys.length; i += 1) {
            const findItemStore = data?.data.find(
                (item) => item.id === newSelectedRowKeys[i],
            );

            if (!findItemStore) {
                // eslint-disable-next-line no-continue
                continue;
            }

            const storeSelected = storesCheckedArray.find(
                (item) => item.id === newSelectedRowKeys[i],
            );

            if (findItemStore && !storeSelected) {
                storesCheckedArray.push(findItemStore);
            }
        }

        if (newSelectedRowKeys.length !== storesCheckedArray.length) {
            const filteringUncheckedLines = storesCheckedArray.filter(
                (item) => {
                    if (newSelectedRowKeys.includes(item.id)) {
                        return item;
                    }
                },
            );
            storesCheckedArray = filteringUncheckedLines;
        }

        setStoresChecked(storesCheckedArray);

        if (newSelectedRowKeys.length === 1) {
            const findSingleStore = data?.data.find(
                (item) => item.id === newSelectedRowKeys[0],
            );

            if (findSingleStore) {
                setShopManagersEdit({
                    active: !!findSingleStore.active,
                    opened: !!findSingleStore.is_open,
                    shopManagerBlocked:
                        !!findSingleStore.shopManager.is_blocked,
                });

                setStatusStores({
                    active: findSingleStore.active === 1 ? 100 : 0,
                    isBlocked:
                        findSingleStore.shopManager.is_blocked === 0 ? 100 : 0,
                    opened: findSingleStore.is_open === 1 ? 100 : 0,
                });
            }
        } else {
            setShopManagersEdit({
                active: false,
                opened: false,
                shopManagerBlocked: false,
            });
            setStatusStores({
                active: 50,
                isBlocked: 50,
                opened: 50,
            });
        }
    };

    const rowSelection = {
        onChange: onSelectChange,
        preserveSelectedRowKeys: true,
        selectedRowKeys: storesSelected,
    };

    const onSubmit = (data: any) => {
        setFilters({
            ...filters,
            ...data,
            firstSaleDate: formatYearMonth(data?.firstSaleDate),
        });
        setMeta({
            ...meta,
            page: 1,
        });
        setStoresSelected([]);
    };

    const resetStatedOfEditing = () => {
        if (storesSelected.length > 1) {
            setShopManagersEdit({
                active: false,
                opened: false,
                shopManagerBlocked: false,
            });
        }
    };

    const formatValue = (value: number) => {
        return currencyFormatter(value / 100);
    };
    const formatVariation = (value: number) => {
        if (Number.isInteger(value * 100)) {
            return `${value}%`;
        }

        return `${(value * 100).toFixed(2)}%`;
    };

    const onChangeSlide = (
        value: number,
        type: 'blocked' | 'active' | 'open',
    ) => {
        switch (type) {
            case 'active':
                setShopManagersEdit({
                    ...shopManagersEdit,
                    active: value === 100,
                });
                setStatusStores({
                    ...statusStores,
                    active: value,
                });
                break;
            case 'open':
                setShopManagersEdit({
                    ...shopManagersEdit,
                    opened: value === 100,
                });
                setStatusStores({
                    ...statusStores,
                    opened: value,
                });
                break;
            case 'blocked':
                setShopManagersEdit({
                    ...shopManagersEdit,
                    shopManagerBlocked: value < 50,
                });
                setStatusStores({
                    ...statusStores,
                    isBlocked: value,
                });
                break;
            default:
                break;
        }
    };

    const formatYearMonth = (date?: string) =>
        date ? dayjs(date).format('YYYY-MM') : undefined;

    const generateExelStore = async () => {
        try {
            await useReportStoreMutation({
                name: filters.name,
                city: filters.city,
                state: filters.state,
                shopmanagerEmail: filters.shopmanagerEmail,
                shopmanagerId: filters.shopmanagerId,
                storeId: filters.storeId,
                totemEnum: filters.totemEnum,
                active: filters.active,
                open: filters.open,
                inventory: filters.inventory,
                totem: filters.totem,
                group: null,
                reportType: 'stores',
                isTotemAuthenticated: filters?.isTotemAuthenticated,
                firstSaleDate: formatYearMonth(filters?.firstSaleDate),
                saleInLastSevenDays: filters?.saleInLastSevenDays || '',
            });
            message.success(
                'O relatório de lojas está sendo processado. Por favor, aguarde a conclusão.',
            );
        } catch (err) {
            if (isRecaptchaNotResolved(err)) return;
            message.error(
                'Aconteceu algum problema, tente novamente mais tarde.',
            );
        }
    };

    useEffect(() => {
        titleValue('Gerenciamento de lojas');
    }, []);

    return (
        <Row style={{ padding: breakpoints.md ? '5px 42px' : '5px 12px' }}>
            <S.GlobalStyleStoreManager />
            <Col span={24}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onSubmit}
                    initialValues={initialValues}
                >
                    <Row gutter={24} className="clear-margin">
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6 }}
                        >
                            <Form.Item label="Nome da loja" name="name">
                                <Space
                                    direction="vertical"
                                    style={{ width: '100%' }}
                                >
                                    <Input />
                                </Space>
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6 }}
                        >
                            <Form.Item
                                label="ID da loja"
                                name="storeId"
                                rules={[
                                    {
                                        type: 'number',
                                        message:
                                            'Por favor, informe um número válido!',
                                    },
                                ]}
                            >
                                <InputNumber style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6 }}
                        >
                            <Form.Item
                                label="ID Lojista"
                                name="shopmanagerId"
                                rules={[
                                    {
                                        type: 'number',
                                        message:
                                            'Por favor, informe um número válido!',
                                    },
                                ]}
                            >
                                <InputNumber style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6 }}
                        >
                            <Form.Item
                                label="Email Lojista"
                                name="shopmanagerEmail"
                                rules={[
                                    {
                                        type: 'email',
                                        message:
                                            'Por favor, informe um email válido!',
                                    },
                                ]}
                            >
                                <Space
                                    direction="vertical"
                                    style={{ width: '100%' }}
                                >
                                    <Input />
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Collapse ghost>
                        <Panel header="Filtros avançados" key="1" forceRender>
                            <FilterStoreManagement />
                        </Panel>
                    </Collapse>
                    <Row wrap gutter={[19, 10]}>
                        <Col
                            style={{
                                width: breakpoints.md ? undefined : '100%',
                            }}
                        >
                            <Button
                                htmlType="submit"
                                className="default-btn"
                                style={{ width: breakpoints.md ? 227 : '100%' }}
                            >
                                Pesquisar
                            </Button>
                        </Col>
                        <Col
                            style={{
                                width: breakpoints.md ? undefined : '100%',
                            }}
                        >
                            <Button
                                htmlType="reset"
                                danger
                                style={{ width: breakpoints.md ? 227 : '100%' }}
                                onClick={() => {
                                    setFilters({
                                        city: '',
                                        name: '',
                                        shopmanagerEmail: undefined,
                                        state: '',
                                        shopmanagerId: undefined,
                                        open: undefined,
                                        active: undefined,
                                        inventory: undefined,
                                        isTotemAuthenticated: null,
                                        firstSaleDate: undefined,
                                    });
                                    setMeta({
                                        page: 1,
                                        last_page: 1,
                                        total: 0,
                                        perPage: 10,
                                    });
                                    setInitialValues({
                                        open: undefined,
                                        active: undefined,
                                        inventory: undefined,
                                        saleInLastSevenDays: '',
                                        orderBy: undefined,
                                        billingType: null,
                                        orderDir: undefined,
                                    });
                                    setStoresSelected([]);
                                }}
                            >
                                Limpar
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Col>
            <Col span={23}>
                <Divider style={{ background: '#DADADA' }} />
            </Col>
            <Col span={24}>
                <Row gutter={24}>
                    <Col
                        className="gutter-row"
                        xs={24}
                        md={12}
                        lg={{ flex: '20%' }}
                        xl={{ flex: '20%' }}
                        xxl={{ flex: '20%' }}
                        style={{ marginBottom: 16 }}
                    >
                        <S.CardInfoContainer>
                            <S.CardInfoHeader>
                                <S.CardInfoHeaderTitle>
                                    Número de Lojas
                                </S.CardInfoHeaderTitle>
                            </S.CardInfoHeader>
                            <S.CardInfoFooter>
                                <S.CardInfoFooterTitle
                                    style={{ fontSize: fontSizeDynamic }}
                                >
                                    {!isLoadingSummary &&
                                        (dataSummary?.stores || 0)}

                                    {isLoadingSummary && (
                                        <Skeleton.Button size="large" />
                                    )}
                                </S.CardInfoFooterTitle>
                            </S.CardInfoFooter>
                        </S.CardInfoContainer>
                    </Col>
                    <Col
                        className="gutter-row"
                        xs={24}
                        md={12}
                        lg={{ flex: '20%' }}
                        xl={{ flex: '20%' }}
                        xxl={{ flex: '20%' }}
                        style={{ marginBottom: 16 }}
                    >
                        <S.CardInfoContainer>
                            <S.CardInfoHeader>
                                <S.CardInfoHeaderTitle>
                                    Vendas (m-1)
                                </S.CardInfoHeaderTitle>
                            </S.CardInfoHeader>
                            <S.CardInfoFooter>
                                <ResponsiveText
                                    min={14}
                                    max={28}
                                    isLoading={isLoadingSummary}
                                    onFontSizeChange={setFontSizeDynamic}
                                    skeleton={<Skeleton.Input />}
                                >
                                    {formatValue(
                                        dataSummary?.monthMinusOne || 0,
                                    )}
                                </ResponsiveText>
                            </S.CardInfoFooter>
                        </S.CardInfoContainer>
                    </Col>

                    <Col
                        className="gutter-row"
                        xs={24}
                        md={12}
                        lg={{ flex: '20%' }}
                        xl={{ flex: '20%' }}
                        xxl={{ flex: '20%' }}
                        style={{ marginBottom: 16 }}
                    >
                        <S.CardInfoContainer>
                            <S.CardInfoHeader>
                                <S.CardInfoHeaderTitle>
                                    Vendas (m-2)
                                </S.CardInfoHeaderTitle>
                            </S.CardInfoHeader>
                            <S.CardInfoFooter>
                                <ResponsiveText
                                    min={14}
                                    max={28}
                                    isLoading={isLoadingSummary}
                                    skeleton={<Skeleton.Input />}
                                >
                                    {formatValue(
                                        dataSummary?.monthMinusTwo || 0,
                                    )}
                                </ResponsiveText>
                            </S.CardInfoFooter>
                        </S.CardInfoContainer>
                    </Col>

                    <Col
                        className="gutter-row"
                        xs={24}
                        md={12}
                        lg={{ flex: '20%' }}
                        xl={{ flex: '20%' }}
                        xxl={{ flex: '20%' }}
                        style={{ marginBottom: 16 }}
                    >
                        <S.CardInfoContainer>
                            <S.CardInfoHeader>
                                <S.CardInfoHeaderTitle>
                                    Vendas (m-3)
                                </S.CardInfoHeaderTitle>
                            </S.CardInfoHeader>
                            <S.CardInfoFooter>
                                <ResponsiveText
                                    min={14}
                                    max={28}
                                    isLoading={isLoadingSummary}
                                    skeleton={<Skeleton.Input />}
                                >
                                    {formatValue(
                                        dataSummary?.monthMinusThree || 0,
                                    )}
                                </ResponsiveText>
                            </S.CardInfoFooter>
                        </S.CardInfoContainer>
                    </Col>

                    <Col
                        className="gutter-row"
                        xs={24}
                        md={12}
                        lg={{ flex: '20%' }}
                        xl={{ flex: '20%' }}
                        xxl={{ flex: '20%' }}
                    >
                        <S.CardInfoContainer>
                            <S.CardInfoHeader>
                                <S.CardInfoHeaderTitle>
                                    Variação
                                </S.CardInfoHeaderTitle>
                            </S.CardInfoHeader>
                            <S.CardInfoFooter>
                                <S.CardInfoFooterTitle
                                    style={{ fontSize: fontSizeDynamic }}
                                >
                                    {!isLoadingSummary &&
                                        formatVariation(
                                            dataSummary?.variation || 0,
                                        )}

                                    {isLoadingSummary && (
                                        <Skeleton.Button size="large" />
                                    )}
                                </S.CardInfoFooterTitle>
                            </S.CardInfoFooter>
                        </S.CardInfoContainer>
                    </Col>
                </Row>
            </Col>
            <Col span={23}>
                <Divider style={{ background: '#DADADA' }} />
            </Col>
            <Col span={24}>
                <S.AreaExported>
                    <Button
                        className="green-btn"
                        style={{
                            width: breakpoints.md ? 227 : '100%',
                            marginBottom: 20,
                        }}
                        onClick={generateExelStore}
                        loading={isLoadingReport}
                    >
                        Exportar
                    </Button>
                    <S.AreaListItem>
                        {exportedExcel?.data?.map((file_generated, idx) => {
                            const url = file_generated.uploadedFile?.url;
                            const filename = file_generated.uploadedFile?.name;

                            const handleDownload = async () => {
                                try {
                                    await handleDownloadFile(url, filename);
                                } catch (error) {
                                    message.error(
                                        'Falha ao baixar o arquivo, tente novamente ou fale com o nosso suporte.',
                                    );
                                }
                            };
                            return (
                                <Button
                                    type="link"
                                    style={{
                                        width: breakpoints.md ? 'auto' : '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        fontSize: 16,
                                        color:
                                            file_generated.status ===
                                            'completed'
                                                ? '#2081FF'
                                                : '#B0B0B0',
                                        textAlign: 'start',
                                        marginBottom: 40,
                                        whiteSpace: 'normal',
                                        lineHeight: 1.35,
                                    }}
                                    onClick={handleDownload}
                                    key={idx}
                                    disabled={
                                        file_generated.status !== 'completed'
                                    }
                                >
                                    <span
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            whiteSpace: 'normal',
                                            overflowWrap: 'anywhere',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {`${
                                            file_generated?.uploadedFile?.name.replace(
                                                'report-',
                                                '',
                                            ) ||
                                            `store_${moment(
                                                file_generated?.created_at,
                                            ).format('YYYY-MM-DD')}.xlsx`
                                        }`}
                                        <DownloadOutlined
                                            style={{ marginLeft: 8 }}
                                        />
                                    </span>
                                    <span
                                        style={{
                                            color: '#B0B0B0',
                                            fontSize: 16,
                                            display: 'block',
                                            width: '100%',
                                            whiteSpace: 'normal',
                                            overflowWrap: 'anywhere',
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {getStatusText(file_generated.status)}
                                    </span>
                                </Button>
                            );
                        })}
                    </S.AreaListItem>
                </S.AreaExported>
            </Col>
            <Col span={24}>
                <Row justify="space-between">
                    <Button
                        type="primary"
                        className="default-btn"
                        style={{ width: breakpoints.md ? 227 : '100%' }}
                        disabled={storesSelected.length <= 0}
                        onClick={() => {
                            setOpenModal(true);
                        }}
                    >
                        Editar
                    </Button>
                </Row>
            </Col>
            <Col>
                <Row>
                    <S.ItemSelectedTitle style={{ color: token.colorText }}>
                        Itens selecionados: {storesSelected.length}
                    </S.ItemSelectedTitle>
                </Row>
                <Row>
                    <S.QuantityResult style={{ color: token.colorText }}>
                        A pesquisa retornou {data?.data?.length} resultado(s)
                    </S.QuantityResult>
                </Row>
                <Row>
                    <div
                        style={{
                            width: breakpoints.md ? width : '100%',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                        }}
                    >
                        <TableStoreMAnagement
                            loading={isLoading}
                            dataSource={data?.data || []}
                            rowSelection={rowSelection}
                            setFilters={setFilters}
                        />
                    </div>
                    <div
                        style={{
                            width: '100%',
                            height: 40,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: 20,
                        }}
                    >
                        <Pagination
                            total={data?.meta.total}
                            current={meta.page}
                            onChange={(page, pzSize) => {
                                setCurrentPage(page, pzSize);
                            }}
                        />
                    </div>
                </Row>
            </Col>

            <Modal
                open={openMpdal}
                onCancel={() => {
                    setOpenModal(false);
                    resetStatedOfEditing();
                }}
                centered
                title={
                    <div style={{ fontSize: 16, fontWeight: 600 }}>Editar</div>
                }
                footer={[
                    <Row justify="space-between">
                        <Button
                            htmlType="reset"
                            danger
                            style={{ width: 227 }}
                            onClick={() => {
                                setOpenModal(false);
                                resetStatedOfEditing();
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            htmlType="submit"
                            className="default-btn"
                            loading={isLoadingEditStore}
                            style={{ width: 227 }}
                            onClick={handleEditStores}
                        >
                            {isLoadingEditStore ? 'Salvando' : 'Salvar'}
                        </Button>
                    </Row>,
                ]}
            >
                <Col>
                    <Row justify="center">
                        <S.Label>Desbloqueio de Lojista</S.Label>
                    </Row>
                    <S.AreaSlide>
                        <S.AreaLabel>
                            <S.LabelRange style={{ left: 0 }}>
                                Bloqueado
                            </S.LabelRange>
                        </S.AreaLabel>

                        <Slider
                            defaultValue={50}
                            step={50}
                            disabled={false}
                            dots={false}
                            min={0}
                            style={{ width: 148 }}
                            value={statusStores.isBlocked}
                            max={100}
                            included
                            tooltip={{
                                formatter: null,
                            }}
                            trackStyle={{ background: 'transparent' }}
                            onChange={(value) =>
                                onChangeSlide(value, 'blocked')
                            }
                        />

                        <S.AreaLabel>
                            <S.LabelRange>Desbloqueado</S.LabelRange>
                        </S.AreaLabel>
                    </S.AreaSlide>
                </Col>
                <Col style={{ marginTop: 25 }}>
                    <Row justify="center">
                        <S.Label>Ativação de Loja</S.Label>
                    </Row>
                    <S.AreaSlide>
                        <S.AreaLabel>
                            <S.LabelRange style={{ left: 0 }}>
                                Desativa
                            </S.LabelRange>
                        </S.AreaLabel>

                        <Slider
                            defaultValue={50}
                            disabled={false}
                            dots={false}
                            step={50}
                            min={0}
                            style={{ width: 148 }}
                            max={100}
                            value={statusStores.active}
                            included
                            trackStyle={{ background: 'transparent' }}
                            tooltip={{
                                formatter: null,
                            }}
                            onChange={(value) => onChangeSlide(value, 'active')}
                        />

                        <S.AreaLabel>
                            <S.LabelRange>Ativa</S.LabelRange>
                        </S.AreaLabel>
                    </S.AreaSlide>
                </Col>
                <Col style={{ marginTop: 25 }}>
                    <Row justify="center">
                        <S.Label>Abertura de Loja</S.Label>
                    </Row>
                    <S.AreaSlide>
                        <S.AreaLabel>
                            <S.LabelRange style={{ left: 0 }}>
                                Fechada
                            </S.LabelRange>
                        </S.AreaLabel>

                        <Slider
                            defaultValue={50}
                            disabled={false}
                            dots={false}
                            step={50}
                            min={0}
                            style={{ width: 148 }}
                            max={100}
                            value={statusStores.opened}
                            included
                            tooltip={{
                                formatter: null,
                            }}
                            trackStyle={{ background: 'transparent' }}
                            onChange={(value) => onChangeSlide(value, 'open')}
                        />

                        <S.AreaLabel>
                            <S.LabelRange>Aberta</S.LabelRange>
                        </S.AreaLabel>
                    </S.AreaSlide>
                </Col>
            </Modal>
        </Row>
    );
};

export default StoreManagementPage;
